// Pure helpers over market-data series from the market-data proxy
// (core/market-data.js → scraper/src/market-data.mjs). A series is a list of
// `[date 'YYYY-MM-DD', value]` points on trading days only.
//
// Series ids are source-qualified:
//   nasdaq:price:<TICKER>           daily close, USD, split-adjusted
//   vanguard:nav|buy|sell:<portId>  fund unit price, AUD
//   rba:fx:AUDUSD                   RBA F11 "A$1 = x USD", as published

/** The RBA USD exchange-rate series (A$1 = x USD). */
export const RBA_AUDUSD = 'rba:fx:AUDUSD';

/**
 * A lookup over a series: `(day) => value` on or before `day` (non-trading
 * days use the latest prior point; days after the data use the latest known
 * value), or null before the first point / with no data.
 */
export function seriesLookup(points) {
  const sorted = [...(points ?? [])]
    .filter((p) => Array.isArray(p) && typeof p[0] === 'string' && Number.isFinite(p[1]))
    .sort((a, b) => a[0].localeCompare(b[0]));
  return (day) => {
    let lo = 0;
    let hi = sorted.length - 1;
    let found = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (sorted[mid][0] <= day) {
        found = sorted[mid][1];
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return found;
  };
}

/** The newest point's value, or null. */
export function latestValue(points) {
  let best = null;
  for (const p of points ?? []) {
    if (Array.isArray(p) && Number.isFinite(p[1]) && (!best || p[0] > best[0])) best = p;
  }
  return best ? best[1] : null;
}

/** AUD per 1 USD from an RBA "A$1 = x USD" value (null-safe). */
export function audPerUsd(audUsd) {
  return Number.isFinite(audUsd) && audUsd > 0 ? 1 / audUsd : null;
}

/**
 * An FX provider for the pay timeline from the RBA series: `{ rateOn(day) →
 * AUD per USD | null, latest: AUD per USD | null }`. An empty / missing series
 * gives a provider that always returns null (callers treat that as "no rate").
 */
export function usdAudProvider(points) {
  const lookup = seriesLookup(points);
  return {
    rateOn: (day) => audPerUsd(lookup(day)),
    latest: audPerUsd(latestValue(points)),
  };
}

/**
 * AU$ per 1 unit of `currency`: 1 for AUD; else an entered `rate` (it wins);
 * else, for USD, the market `usdRate` (AUD per USD); else null — no rate for
 * that pair (callers count the value as unknown and warn).
 */
export function audRateFor(currency, { rate = null, usdRate = null } = {}) {
  if (currency === 'AUD') return 1;
  if (rate > 0) return rate;
  if (currency === 'USD' && usdRate > 0) return usdRate;
  return null;
}

/** A provider with no rates (feature off / fetch failed). */
export const NO_FX = Object.freeze({ rateOn: () => null, latest: null });
